/**
 * @x9/usage — usage-ledger constants and pure helpers shared by mobile and
 * Cloud Functions.
 *
 * Product source: docs/meta/prd-usage-ledger-and-gates-2026-08.md (WS1).
 * Phase 1 is TRACKING-ONLY: counters + shadow triggers, no enforcement.
 * All Firestore writes are server-side (functions/src/usage/ledger.js).
 */

'use strict';

/** Billing/product timezone — month buckets roll over on São Paulo midnight. */
const USAGE_TIMEZONE = 'America/Sao_Paulo';

/** Firestore collection names. */
const USAGE_COLLECTIONS = {
  LEDGERS: 'usageLedgers',
  MONTHS: 'months',
  TRIGGERS: 'monetisationTriggers',
};

/**
 * Counter field names inside usageLedgers/{uid}/months/{YYYY-MM}.counters.
 * Monthly rollups; lifetime totals mirrored on the root doc.
 */
const COUNTERS = {
  RUNS_DISPATCHED: 'runsDispatched',
  RUNS_DISPATCHED_SELF: 'runsDispatchedSelf',
  RUNS_DISPATCHED_TO_OTHERS: 'runsDispatchedToOthers',
  DISTINCT_EXECUTORS: 'distinctExecutors',
  INBOUND_RECEIVED: 'inboundReceived',
  INBOUND_ACCEPTED: 'inboundAccepted',
  INBOUND_HELD: 'inboundHeld',
  BUCKETS_CREATED: 'bucketsCreated',
  BUCKETS_CREATED_ROUTINE: 'bucketsCreatedRoutine',
  QR_TOKENS_CREATED: 'qrTokensCreated',
  AI_CALLS: 'aiCalls',
  RUN_REPORT_EXPORTS: 'runReportExports',
  TASKS_CREATED: 'tasksCreated',
};

/** Trigger doc `type` values (see PRD trigger ladder). */
const TRIGGER_TYPES = {
  LIMIT_HEADS_UP: 'limit_heads_up',
  LIMIT_REACHED: 'limit_reached',
  DISPATCH_BLOCKED: 'dispatch_blocked',
  CONTRIBUTIONS_HELD: 'contributions_held',
};

/**
 * Metered ladders — the ONLY counters that raise limit triggers.
 *
 * Each entry names the `users.freeLimitsSnapshot` field that holds the
 * account's real allowance, so the ladder measures against what that account
 * was actually promised (grandfathering contract) instead of a placeholder.
 * `fallback` covers accounts stamped before snapshots existed; a snapshot
 * value of `null` means unlimited and raises nothing.
 *
 * DELIBERATELY ABSENT (2026-09-10) — seats:
 * `distinctExecutors` and `runsDispatchedToOthers` used to carry placeholder
 * limits of 2 and 5. Both contradicted the shipped plan. The free plan is
 * "me + one external seat, GLOBALLY": `seatPlans/{uid}` is a persistent
 * roster of `freeExecutorSeats` (1) slots, self-dispatch never consumes one,
 * and re-dispatching to a seated person is free — so a monthly count of
 * distinct executors measures nothing the plan charges for, and delegating
 * repeatedly to your one teammate has no limit at all. The real signal is
 * already live: `ensureSeatForDispatch` blocks a *second* teammate and writes
 * a non-shadow `dispatch_blocked` trigger. Do not re-add a seat meter here
 * without retiring that gate first.
 */
const METERED_LADDERS = {
  [COUNTERS.INBOUND_ACCEPTED]: {
    allowanceField: 'inboundSubmissionsMonth',
    fallback: 5,
  },
};

/**
 * Trigger ladder thresholds (fractions of the allowance). 50% is ledger-only.
 * Ordered ascending by severity — `ladderCrossings` relies on that.
 */
const LADDER = [
  { fraction: 0.8, type: TRIGGER_TYPES.LIMIT_HEADS_UP },
  { fraction: 1.0, type: TRIGGER_TYPES.LIMIT_REACHED },
];

/**
 * Client-reportable usage events (recordUsageEvent callable whitelist).
 * Maps event type → counter.
 */
const USAGE_EVENT_TYPES = {
  runReportExport: COUNTERS.RUN_REPORT_EXPORTS,
};

/**
 * Plan-terms versions (grandfathering — WS1 Phase 4). Bump the CURRENT
 * version when the commercial terms / free-tier definition materially
 * changes. Distinct from the legal TERMS_VERSION in AuthContext.
 *
 * '2026-08'   — pre-monetisation baseline (everything free/unlimited).
 * '2026-08.2' — settled meters (2026-08-10): free = solo unlimited, 0
 *               executor seats, 50 inbound contributions/month pooled.
 * '2026-08.3' — free inbound lowered to 5/month (feel the app; testable).
 * '2026-08.4' — Equipe bundled inbound 250 → 150 (volume packs stay +250).
 *
 * These constants are code DEFAULTS; the live values for future signups come
 * from Firestore `config/planTerms` (editable in master.x9 — WS6) via
 * functions/src/planTerms.js, stamped server-side at user-doc creation.
 * Editing config NEVER changes existing users' snapshots.
 */
const PLAN_TERMS_VERSION = '2026-08.4';

/**
 * LEGACY baseline — the "what was free when I joined" record for accounts
 * that predate plan-terms stamping (login backfill path only). `null` =
 * unlimited. Never change this: it is the grandfather contract.
 */
const FREE_BASELINE_SNAPSHOT = {
  planTermsVersion: '2026-08',
  bucketCap: null,
  executorSeatsMonth: null,
  inboundSubmissionsMonth: null,
};

/**
 * CURRENT free limits (defaults for PLAN_TERMS_VERSION) — stamped onto NEW
 * signups. One named teammate seat; 5 inbound contributions/month.
 */
const CURRENT_FREE_SNAPSHOT = {
  planTermsVersion: PLAN_TERMS_VERSION,
  bucketCap: null,
  executorSeatsMonth: 1,
  inboundSubmissionsMonth: 5,
};

/**
 * "YYYY-MM" month key for a date in the product timezone.
 * @param {Date|number|string} [date]
 * @returns {string}
 */
function monthKey(date = new Date()) {
  const d = date instanceof Date ? date : new Date(date);
  // en-CA yields ISO-like "YYYY-MM-DD"; slice the month prefix.
  const iso = new Intl.DateTimeFormat('en-CA', {
    timeZone: USAGE_TIMEZONE,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).format(d);
  return iso.slice(0, 7);
}

/**
 * Classify a routine-run dispatch for the delegation line.
 * Self-assigned runs stay free/unlimited; runs to others consume seats
 * (enforcement OFF in Phase 1 — classification feeds the ledger only).
 *
 * @param {{ ownerUserId?: string, executorUserId?: string, executorContactId?: string }} run
 * @returns {'self'|'other'|'unassigned'|'unknown'}
 */
function classifyRunDispatch(run) {
  const owner = run && run.ownerUserId ? String(run.ownerUserId) : null;
  const execUser = run && run.executorUserId ? String(run.executorUserId) : null;
  const execContact = run && run.executorContactId ? String(run.executorContactId) : null;
  if (!owner) return 'unknown';
  if (execUser && execUser === owner) return 'self';
  if (execUser || execContact) return 'other';
  return 'unassigned';
}

/**
 * Stable identity key for a dispatched-to executor (distinct-executor set).
 * Prefers the registered uid; falls back to the contact card id.
 * Returns null for self/unassigned dispatches.
 *
 * @param {{ ownerUserId?: string, executorUserId?: string, executorContactId?: string }} run
 * @returns {string|null}
 */
function executorKeyForRun(run) {
  if (classifyRunDispatch(run) !== 'other') return null;
  if (run.executorUserId) return `u:${run.executorUserId}`;
  if (run.executorContactId) return `c:${run.executorContactId}`;
  return null;
}

/**
 * EVERY identity key the dispatched-to executor is known by, preferred first.
 *
 * One person can be reached under two keys across a month: `c:<contactId>`
 * while their card is unlinked, then `u:<uid>` once they sign up and the card
 * links. Both must map to a single executor or the ledger counts them twice.
 *
 * @param {{ ownerUserId?: string, executorUserId?: string, executorContactId?: string }} run
 * @returns {string[]} Empty for self/unassigned dispatches.
 */
function executorKeysForRun(run) {
  if (classifyRunDispatch(run) !== 'other') return [];
  const keys = [];
  if (run.executorUserId) keys.push(`u:${run.executorUserId}`);
  if (run.executorContactId) keys.push(`c:${run.executorContactId}`);
  return keys;
}

/**
 * Canonical executor a key resolves to in a stored `executorKeys` map.
 * Legacy entries are `true` (the key is its own canonical); aliased entries
 * hold the canonical key as a string.
 *
 * @param {Record<string, string|boolean>} executorKeys
 * @param {string} key
 * @returns {string|null}
 */
function canonicalExecutor(executorKeys, key) {
  const value = (executorKeys || {})[key];
  if (typeof value === 'string' && value) return value;
  return value ? key : null;
}

/**
 * Number of distinct people in a stored `executorKeys` map, collapsing
 * aliases of the same executor.
 *
 * @param {Record<string, string|boolean>} executorKeys
 * @returns {number}
 */
function distinctExecutorCount(executorKeys) {
  const people = new Set();
  for (const [key, value] of Object.entries(executorKeys || {})) {
    people.add(typeof value === 'string' && value ? value : key);
  }
  return people.size;
}

/**
 * The ladder rung crossed when a meter moves from `before` to `after`
 * against `limit`. Pure; used by the trigger evaluator.
 *
 * Returns AT MOST ONE rung — the most severe crossed. A jump that clears
 * several rungs at once (or an allowance so small the rungs collapse, e.g.
 * 80% of 1) must not fire "you're near the limit" and "you're at the limit"
 * seconds apart; once you are at the limit the heads-up is worthless.
 *
 * @param {number} before
 * @param {number} after
 * @param {number} limit
 * @returns {Array<{ fraction: number, type: string }>} 0 or 1 entries.
 */
function ladderCrossings(before, after, limit) {
  if (!Number.isFinite(limit) || limit <= 0) return [];
  const b = Number.isFinite(before) ? before : 0;
  const a = Number.isFinite(after) ? after : 0;
  if (a <= b) return [];
  const crossed = LADDER.filter(({ fraction }) => {
    const threshold = fraction * limit;
    return b < threshold && a >= threshold;
  });
  return crossed.length ? [crossed[crossed.length - 1]] : [];
}

module.exports = {
  USAGE_TIMEZONE,
  USAGE_COLLECTIONS,
  COUNTERS,
  TRIGGER_TYPES,
  METERED_LADDERS,
  LADDER,
  USAGE_EVENT_TYPES,
  PLAN_TERMS_VERSION,
  FREE_BASELINE_SNAPSHOT,
  CURRENT_FREE_SNAPSHOT,
  monthKey,
  classifyRunDispatch,
  executorKeyForRun,
  executorKeysForRun,
  canonicalExecutor,
  distinctExecutorCount,
  ladderCrossings,
};
